import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTenant } from 'core';
import { getEnv } from 'config';
import { useHistory } from 'react-router-dom';
import { api as backOfficeApi } from 'react-structure-admin';
import {
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
  JsonHubProtocol
} from '@microsoft/signalr';
import { notification } from 'antd';
import truncate from 'util/functions/truncate';

const SignalRContext = createContext();

export const useSignalR = () => useContext(SignalRContext);

export const SignalRProvider = ({ children }) => {
  const baseUrl = backOfficeApi.getBaseUrl().replace(/(\/v1)(\/.*$)/, '$1');
  const { tenant } = useTenant();
  const history = useHistory();
  const [connection, setConnection] = useState(null);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    // Toggle de dev: com REACT_APP_DISABLE_SIGNALR=true não abrimos a conexão. A reconexão
    // automática do SignalR martela o backend enquanto ele está pausado no debugger, e no macOS
    // isso dispara um bug de accept de socket do runtime .NET que derruba o processo. Desligar aqui
    // corta o gatilho sem afetar produção (a flag só existe no ambiente local).
    if (getEnv('REACT_APP_DISABLE_SIGNALR') === 'true') {
      return undefined;
    }

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/push-notification`, {
        accessTokenFactory: () => localStorage.getItem('auth_accesstoken'),
        withCredentials: false
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .withHubProtocol(new JsonHubProtocol())
      .build();

    newConnection.start().then(() => {
      setConnection(newConnection);
    });

    return () => {
      if (newConnection.state === HubConnectionState.Connected) {
        newConnection.invoke('RemoveGroupAsync', `${tenant.id}`).finally(() => {
          newConnection.stop();
        });
      }
    };
  }, []);

  useEffect(() => {
    if (!connection || connection.state !== HubConnectionState.Connected) {
      return;
    }

    connection.invoke('JoinGroupAsync', `${tenant.id}`);
  }, [connection]);

  useEffect(() => {
    if (!connection) {
      return;
    }

    connection.on('ReceiveNotification', (newNotification) => {
      api.info({
        className: 'gx-pointer',
        message: (
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {newNotification.title}
          </div>
        ),
        description: (
          <div style={{ fontSize: 14 }}>
            {truncate(newNotification.description, 120, '....')}
          </div>
        ),
        placement: 'bottomRight',
        onClick: () => history.push('/p/notifications')
      });
    });

    return () => {
      connection.off('ReceiveNotification');
    };
  }, [connection]);

  return (
    <SignalRContext.Provider value={{ connection }}>
      {contextHolder}
      {children}
    </SignalRContext.Provider>
  );
};
